/**
 * Utility functions for processing images before upload
 * Enhanced with WebP support and optimizations
 */
import { isWebPSupported, convertToWebP } from "./imageOptimizer";

/**
 * Resize an image to fit within maximum dimensions while maintaining aspect ratio
 * @param {File} file - The image file to resize
 * @param {Object} options - Resize options
 * @param {number} options.maxWidth - Maximum width (default 1200)
 * @param {number} options.maxHeight - Maximum height (default 1200)
 * @param {number} options.quality - Image quality 0-1 (default 0.8)
 * @param {boolean} options.useWebP - Whether to convert to WebP if supported (default true)
 * @returns {Promise<File>} Resized image as a File object
 */
export const resizeImage = async (file, options = {}) => {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    useWebP = true,
  } = options;

  // Check if WebP is supported and should be used
  const webPSupported = useWebP && (await isWebPSupported());

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Tính toán tỷ lệ resize nếu ảnh vượt quá kích thước cho phép
        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Use WebP if supported, otherwise fallback to JPEG
        const imageType = webPSupported ? "image/webp" : "image/jpeg";
        const fileExtension = webPSupported ? ".webp" : ".jpg";

        // Get original filename without extension
        const originalName =
          file.name.split(".").slice(0, -1).join(".") || file.name;
        const newFileName = `${originalName}${fileExtension}`;

        canvas.toBlob(
          (blob) => {
            resolve(
              new File([blob], newFileName, {
                type: imageType,
                lastModified: Date.now(),
              })
            );
          },
          imageType,
          quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Process an image URL by downloading and resizing it
 * @param {string} imageUrl - URL of the image to process
 * @param {Object} options - Resize options (same as resizeImage)
 * @returns {Promise<{file: File, preview: string}>}
 */
export const processImageUrl = async (imageUrl, options = {}) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    // Extract filename from URL or generate a timestamp-based name
    const fileName =
      imageUrl.split("/").pop()?.split("?")[0] || `image-${Date.now()}.jpg`;

    const file = new File([blob], fileName, {
      type: blob.type || "image/jpeg",
    });

    const resizedFile = await resizeImage(file, options);

    return {
      file: resizedFile,
      preview: URL.createObjectURL(resizedFile),
    };
  } catch (error) {
    console.error("Error processing image URL:", error, imageUrl);
    throw error;
  }
};

/**
 * Process multiple images (both files and URLs)
 * @param {Array<File|string>} images - Array of image files or URLs
 * @param {Object} options - Resize options (same as resizeImage)
 * @returns {Promise<Array<{file: File, preview: string}>>}
 */
export const processMultipleImages = async (images, options = {}) => {
  try {
    return await Promise.all(
      images.map(async (image) => {
        try {
          if (typeof image === "string") {
            return await processImageUrl(image, options);
          } else {
            const resizedFile = await resizeImage(image, options);
            return {
              file: resizedFile,
              preview: URL.createObjectURL(resizedFile),
            };
          }
        } catch (error) {
          console.error("Error processing individual image:", error);
          // Return a placeholder or null for failed images
          return null;
        }
      })
    ).then((results) => results.filter(Boolean)); // Filter out null results
  } catch (error) {
    console.error("Error processing multiple images:", error);
    return [];
  }
};

/**
 * Add loading="lazy" attribute to all images in a container
 * @param {HTMLElement} container - The container element
 */
export const addLazyLoadingToImages = (container) => {
  if (!container) return;

  const images = container.querySelectorAll("img:not([loading])");
  images.forEach((img) => {
    img.setAttribute("loading", "lazy");
  });
};

/**
 * Optimize an image element by setting width and height attributes
 * to prevent layout shifts
 * @param {HTMLImageElement} img - The image element to optimize
 */
export const optimizeImageElement = (img) => {
  if (!img) return;

  // Set loading="lazy" if not already set
  if (!img.hasAttribute("loading")) {
    img.setAttribute("loading", "lazy");
  }

  // Add onload handler to set width and height if not already set
  if (!img.hasAttribute("width") || !img.hasAttribute("height")) {
    img.onload = function () {
      if (!this.hasAttribute("width")) {
        this.setAttribute("width", this.naturalWidth);
      }
      if (!this.hasAttribute("height")) {
        this.setAttribute("height", this.naturalHeight);
      }
    };
  }
};
